import { useEffect, useRef, useState } from 'react'
import { useOmniAuth, type OmniAppId } from '@omni/auth'
import { Button } from '@omni/ui'
import { shellNavigate } from '../lib/navigation'
import { takeReturnTo } from '../lib/return-to'
import { instance } from '../lib/config'
import { setSignInNotice } from '../lib/signin-notice'
import { Splash } from './Splash'
import { LoginScreen } from './LoginScreen'

const APP_IDS: readonly OmniAppId[] = ['mail', 'crm', 'pm']

// Provider sign-in (Microsoft, PKCE) is opt-in per instance. Only those
// instances read ?code= and provider errors here; every other instance keeps
// the callback exactly as it was.
const PROVIDER_SIGN_IN = (instance.frontDoor?.providers?.length ?? 0) > 0

/** Back to the entry screen, replacing the callback URL (so Back does not replay it). */
function backToEntry() {
  window.history.replaceState(null, '', '/')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/**
 * Where the magic link lands.
 *
 * Primary path (scanner-proof): the email carries
 * /auth/callback?token_hash=…&type=email. The hash is redeemed only when the
 * person taps Continue. Outlook Safe Links opens every link in a message and
 * runs the page's script, so redeeming on load let the scanner spend the
 * one-time token (and the 6-digit code that shares it) within seconds of the
 * email arriving (Sam, 12 Oct 2026: "codes say 60 minutes but don't last a
 * minute"). Scanners don't tap buttons.
 *
 * Legacy path: implicit-grant tokens in the URL fragment (#access_token=…) —
 * supabase-js consumes those itself (detectSessionInUrl); we just wait for
 * the session to land before navigating so the hash is never clobbered early.
 */
export function AuthCallback() {
  const { refreshSession, getClient } = useOmniAuth()
  const ran = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const params = new URLSearchParams(window.location.search)
  const tokenHash = params.get('token_hash')
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const providerError = PROVIDER_SIGN_IN ? (params.get('error') ?? hash.get('error')) : null
  const providerErrorDescription = PROVIDER_SIGN_IN
    ? (params.get('error_description') ?? hash.get('error_description'))
    : null
  const pkceCode = PROVIDER_SIGN_IN ? params.get('code') : null
  const raw = params.get('app')
  const app: OmniAppId = APP_IDS.includes(raw as OmniAppId) ? (raw as OmniAppId) : 'mail'
  const [busy, setBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  async function redeem() {
    if (!tokenHash || busy) return
    setBusy(true)
    setLinkError(null)
    try {
      const { error: verifyError } = await getClient(app).auth.verifyOtp({ type: 'email', token_hash: tokenHash })
      if (verifyError) {
        setBusy(false)
        setLinkError(
          verifyError.message.toLowerCase().includes('expired') || verifyError.message.toLowerCase().includes('invalid')
            ? 'That sign-in link has already been used or has expired.'
            : verifyError.message,
        )
        return
      }
      await refreshSession(app)
      shellNavigate(takeReturnTo() ?? '/')
    } catch (err) {
      setBusy(false)
      setLinkError(err instanceof Error ? err.message : 'Sign-in failed.')
    }
  }

  useEffect(() => {
    if (tokenHash) return // redeemed by the Continue button, never on load
    if (ran.current) return
    ran.current = true

    const client = getClient(app)

    // Microsoft said no (sign-ups off and no matching account, cancelled,
    // consent refused): back to the branded entry screen with a plain
    // sentence and the email sign-in, never the raw error.
    if (providerError) {
      setSignInNotice({ error: providerError, description: providerErrorDescription })
      backToEntry()
      return
    }

    // PKCE return from Microsoft: supabase-js exchanges ?code= itself while
    // the client initialises (detectSessionInUrl); getSession waits for that.
    // If it did not land, exchange explicitly once; a failure (no code
    // verifier because it started in another browser, or a spent code) goes
    // back to the entry screen with a plain message.
    if (pkceCode) {
      void (async () => {
        try {
          const { data } = await client.auth.getSession()
          if (!data.session) {
            const { error: exchangeError } = await client.auth.exchangeCodeForSession(pkceCode)
            if (exchangeError) {
              setSignInNotice({ error: 'exchange_failed', description: exchangeError.message })
              backToEntry()
              return
            }
          }
          await refreshSession(app)
          shellNavigate(takeReturnTo() ?? '/')
        } catch (err) {
          setSignInNotice({ error: 'exchange_failed', description: err instanceof Error ? err.message : null })
          backToEntry()
        }
      })()
      return
    }

    async function waitForSession(timeoutMs: number): Promise<boolean> {
      const deadline = Date.now() + timeoutMs
      while (Date.now() < deadline) {
        const { data } = await client.auth.getSession()
        if (data.session) return true
        await new Promise((r) => setTimeout(r, 400))
      }
      return false
    }

    void (async () => {
      try {
        // Legacy fragment flow: give supabase-js time to consume the hash.
        const landed = await waitForSession(10_000)
        if (!landed) {
          setError('That sign-in link did not carry a valid session — request a fresh one.')
          return
        }
        await refreshSession(app)
        shellNavigate(takeReturnTo() ?? '/')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sign-in failed.')
      }
    })()
  }, [refreshSession, getClient, tokenHash, app, providerError, providerErrorDescription, pkceCode])

  if (tokenHash) return <LoginScreen confirm={{ onContinue: redeem, busy, error: linkError, onBack: () => shellNavigate('/') }} />

  if (error) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-surface-0">
        <p className="max-w-sm text-center text-sm text-text-secondary">{error}</p>
        <Button onClick={() => shellNavigate('/')}>Back to sign in</Button>
      </div>
    )
  }
  return <Splash label="Signing you in…" />
}
